import { useEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';

const CHAT_ENDPOINT = import.meta.env.VITE_CHAT_URL ?? 'https://astdz-chat.astdz.workers.dev';
const MAX_MESSAGES = 20;

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** Route: `/dashboard/support`. Chat UI that posts conversation history to the astdz-chat Worker. */
export function SupportPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, busy]);

  async function send() {
    const text = input.trim();
    if (!text || busy || messages.length >= MAX_MESSAGES) return;
    setError(null);
    const next = [...messages, { role: 'user' as const, content: text }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const res = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `error ${res.status}`);
      } else {
        const reply = data?.content?.[0]?.text || '(empty response)';
        setMessages([...next, { role: 'assistant', content: reply }]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  }

  function resetConversation() { setMessages([]); setError(null); }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    send();
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)' }}>
      <div className="text-muted" style={{ marginBottom: 8 }}>── support ──────────────────────────────────────</div>
      <p className="text-muted" style={{ marginBottom: 12 }}>
        ask anything about AstdZ, EMRG, the API, certification, Meshtastic setup, or integration.
      </p>

      <div
        ref={logRef}
        className="box"
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          marginBottom: 12,
          minHeight: 200,
        }}
      >
        {messages.length === 0 && (
          <div className="text-muted" style={{ textAlign: 'center', padding: 24 }}>
            no messages yet. type a question below to start.
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '80%',
              padding: '8px 12px',
              border: '1px solid var(--color-border)',
              background: m.role === 'user' ? 'var(--color-surface)' : 'var(--color-bg-elev)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <div className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 4 }}>
              {m.role === 'user' ? '› you' : '› AstdZ'}
            </div>
            {m.content}
          </div>
        ))}
        {busy && (
          <div className="text-muted" style={{ alignSelf: 'flex-start', fontStyle: 'italic' }}>
            › AstdZ is thinking…
          </div>
        )}
        {error && (
          <div className="text-danger" style={{ alignSelf: 'flex-start' }}>✗ {error}</div>
        )}
      </div>

      {messages.length >= MAX_MESSAGES && <p role="status">Conversation limit reached. Start a new conversation to continue.</p>}
      {messages.length > 0 && <button type="button" className="btn" disabled={busy} onClick={resetConversation}>New conversation</button>}
      <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8 }}>
        <textarea
          className="form-input"
          rows={2}
          placeholder="type your question... (Enter to send, Shift+Enter for newline)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          maxLength={500}
          aria-label="Support question"
          disabled={busy || messages.length >= MAX_MESSAGES}
          style={{ flex: 1, resize: 'vertical' }}
        />
        <button type="submit" className="btn btn-primary" disabled={busy || !input.trim() || messages.length >= MAX_MESSAGES}>
          {busy ? 'sending…' : 'send'}
        </button>
      </form>
    </div>
  );
}
